import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BottomSheet, StatusActionButton } from '../../../shared/ui';
import { paymentsV2Api, type CreatePaymentV2Request } from '../../../api/paymentsV2Api';
import '../../paymentsV2/ui/PaymentsV2Card.css';
import './AccountingPayBottomSheet.css';

export interface AccountingPayTarget {
  siteExecutorId: number;
  executorDisplayName: string;
  balance: number;
}

interface AccountingPayBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  siteId: number;
  target: AccountingPayTarget | null;
  onSuccess: () => void;
}

const moneyOpts: Intl.NumberFormatOptions = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};

function formatRub(n: number): string {
  return `${n.toLocaleString('ru-RU', moneyOpts)} ₽`;
}

/** Округление до ближайших сотен: остаток ≥ 50 — вверх, < 50 — вниз (1169 → 1200, 1149 → 1100) */
export function roundToNearestHundreds(amount: number): number {
  if (amount <= 0) return 0;
  return Math.round(amount / 100) * 100;
}

const AccountingPayBottomSheet: React.FC<AccountingPayBottomSheetProps> = ({
  isOpen,
  onClose,
  siteId,
  target,
  onSuccess,
}) => {
  const [amount, setAmount] = useState<number>(0);
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const amountInputRef = useRef<HTMLInputElement>(null);

  const balance = target ? Number(target.balance) || 0 : 0;
  const suggestedAmount = balance > 0 ? roundToNearestHundreds(balance) : 0;

  useEffect(() => {
    if (!isOpen || !target) return;

    const rowBalance = Number(target.balance) || 0;
    const initialAmount = rowBalance > 0 ? roundToNearestHundreds(rowBalance) : 0;

    setError(null);
    setComment('');
    setDate(new Date().toISOString().split('T')[0]);
    setAmount(initialAmount);
  }, [isOpen, target]);

  useEffect(() => {
    if (!isOpen || !target) return;
    const timer = window.setTimeout(() => {
      const input = amountInputRef.current;
      if (!input) return;
      input.focus({ preventScroll: true });
      input.select();
    }, 400);
    return () => window.clearTimeout(timer);
  }, [isOpen, target]);

  const handleClose = useCallback(() => {
    onClose();
    setError(null);
    setAmount(0);
    setComment('');
  }, [onClose]);

  const handleCreatePayment = useCallback(async () => {
    if (!target || amount <= 0 || !date) {
      setError('Заполните все обязательные поля');
      return;
    }

    setIsCreating(true);
    setError(null);
    try {
      const paymentRequest: CreatePaymentV2Request = {
        siteExecutorId: target.siteExecutorId,
        amount,
        date,
        description: comment || '',
      };
      await paymentsV2Api.createPayment(siteId, paymentRequest);
      onSuccess();
      handleClose();
    } catch (err: unknown) {
      console.error('AccountingPayBottomSheet create payment error', err);
      const data = (err as { response?: { data?: string } })?.response?.data;
      setError(typeof data === 'string' ? data : 'Ошибка при создании выплаты');
    } finally {
      setIsCreating(false);
    }
  }, [target, amount, date, comment, siteId, onSuccess, handleClose]);

  const title = target
    ? `Выплата · ${target.executorDisplayName || 'Исполнитель'}`
    : 'Выплата';

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      size="auto"
      showHandle
      allowSwipeToClose
    >
      <form
        className="create-payment-form"
        onSubmit={(e) => {
          e.preventDefault();
          handleCreatePayment();
        }}
      >
        {error && (
          <div className="error-message">
            <i className="ri-error-warning-line" />
            <span>{error}</span>
          </div>
        )}

        {target && (
          <div className="form-group">
            <span className="form-label-static">Баланс</span>
            <div
              className={`accounting-pay-info-value ${
                balance > 0
                  ? 'accounting-pay-balance-positive'
                  : balance < 0
                    ? 'accounting-pay-balance-negative'
                    : ''
              }`}
            >
              {formatRub(balance)}
            </div>
          </div>
        )}

        <div className="form-group">
          <div className="accounting-pay-amount-row">
            <input
              ref={amountInputRef}
              id="accounting-pay-amount"
              type="text"
              inputMode="decimal"
              enterKeyHint="done"
              autoComplete="off"
              value={amount || ''}
              onChange={(e) => setAmount(parseFloat(e.target.value.replace(',', '.')) || 0)}
              placeholder="Сумма, ₽"
              className="form-input accounting-pay-amount-input"
              aria-label="Сумма выплаты"
              required
            />
            {suggestedAmount > 0 && amount !== suggestedAmount && (
              <button
                type="button"
                className="accounting-pay-suggest-btn"
                onClick={() => setAmount(suggestedAmount)}
              >
                {formatRub(suggestedAmount)}
              </button>
            )}
          </div>
          {suggestedAmount > 0 && (
            <p className="accounting-pay-hint">
              Предложение: округление баланса до ближайших сотен ({formatRub(balance)} → {formatRub(suggestedAmount)})
            </p>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="accounting-pay-date">Дата выплаты *</label>
          <input
            id="accounting-pay-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="form-input"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="accounting-pay-comment">Комментарий</label>
          <textarea
            id="accounting-pay-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Комментарий к выплате (необязательно)"
            className="form-textarea"
            rows={3}
          />
        </div>

        <div className="form-actions">
          <StatusActionButton
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={isCreating}
          >
            Отмена
          </StatusActionButton>
          <StatusActionButton
            type="submit"
            variant="primary"
            disabled={!target || amount <= 0 || !date || isCreating}
            loading={isCreating}
            icon="ri-money-dollar-circle-line"
          >
            Выплатить
          </StatusActionButton>
        </div>
      </form>
    </BottomSheet>
  );
};

export default AccountingPayBottomSheet;
